import type {
	HtmlType,
	EventType,
	TsCodeType,
	NameAndType,
} from '../../types.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import { LeafBlock } from '../leaf-block.js'
import { ScreenEditorInput } from '../decorators/screen-editor-input.js'
import { StepperBlock } from './stepper-block.js'
import { TextOrExpression_assumeExpression } from '../../data/text-or-code.js'
import { TextOrExpression_assumeText } from '../../data/text-or-code.js'
import {
	type IconBlockFontSet,
	IconBlockFontSets,
} from '../other/icon-block.js'
import { BlockWithPrefixIcon } from '../common/block-with-prefix-icon.js'

export const ButtonTypes = {
	'mat-button': 'Basic (without background / elevation / outline)',
	'mat-raised-button': 'Raised (with background & elevation)',
	'mat-stroked-button': 'Stroked (with outline)',
	'mat-flat-button': 'Flat (with background)',
	'mat-icon-button': 'Icon Large (circular; meant to contain icon)',
	'mat-icon-button mat-medium-icon-button':
		'Icon Medium (circular; meant to contain icon)',
	'mat-icon-button mat-small-icon-button':
		'Icon Small (circular; meant to contain icon)',
	'mat-icon-button mat-tiny-icon-button':
		'Icon Tiny (circular; meant to contain icon)',
	'mat-fab': 'Large Floating Action Button (FAB)',
	'mat-mini-fab': 'Small Floating Action Button (Mini FAB)',
	'mat-fab extended': 'Floating Action Button (FAB) with text',
	'mat-menu-item': 'Menu item',
} as const
export type ButtonType = keyof typeof ButtonTypes

export const ButtonStepperFunctions = {
	'': '(none)',
	matStepperNext: 'Next step',
	matStepperPrevious: 'Previous step',
} as const
export type ButtonStepperFunction = keyof typeof ButtonStepperFunctions

@jsonClass('ButtonBlock')
export class ButtonBlock extends LeafBlock implements BlockWithPrefixIcon {
	@jsonProperty(TextOrExpression_assumeExpression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 1,
		label: 'Text',
		codeLanguage: 'ts',
		tsReturnType: () => 'string',
		editorSize: 'singleline',
		isBinding: false,
		isMandatory: () => true,
		alternativeContentAreaName: 'content',
	})
	textCode = new TextOrExpression_assumeExpression('Button', 'T')

	@jsonProperty(TextOrExpression_assumeText)
	@ScreenEditorInput({
		inputType: 'code',
		order: 5,
		label: 'Prefix icon',
		codeLanguage: 'ts',
		alternativeContentAreaName: 'prefix',
		tsReturnType: () => 'string',
		editorSize: 'singleline',
		isBinding: false,
	})
	prefixIconCode = new TextOrExpression_assumeText('', 'T')

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 6,
		label: 'Prefix icon style',
		options: Object.entries(IconBlockFontSets),
	})
	prefixIconFontSet: IconBlockFontSet = 'material-icons'

	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 3,
		label: 'Stepper function',
		options: Object.entries(ButtonStepperFunctions),
		isVisible(blocks, properties, bo) {
			return !!blocks[0].getFirstParent((p) => p instanceof StepperBlock)
		},
	})
	@jsonProperty(String)
	stepperFunction: ButtonStepperFunction = ''

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 101,
		label: 'Button appearance',
		tab: 'Appearance',
		options: Object.entries(ButtonTypes),
	})
	buttonType: ButtonType = 'mat-raised-button'

	canAcceptVariableString = (variable: NameAndType) => true
	acceptVariableString = (variable: NameAndType) =>
		(this.textCode = new TextOrExpression_assumeExpression(variable.name))

	constructor(init: Partial<ButtonBlock> = {}) {
		super()
		this.init(init)
	}

	get allowedEventTypes(): EventType[] {
		return [{ name: 'click' }, { name: 'dblclick' }]
	}

	canHaveColor(): boolean {
		return true
	}

	isBlockWithPrefixIcon(): this is BlockWithPrefixIcon {
		return true
	}

	producePreview(): HtmlType {
		if (this.getContentArea('content')) return ''
		const prefixIconHtml = '' // not working right now;;; this.prefixIconCode.content.trim() && this.prefixIconCode.type == 'T' ? `<mat-icon>${this.prefixIconCode.content}</mat-icon>` : ''
		return `<button tabindex="-1">${prefixIconHtml} ${this.exampleText || this.textCode?.content || 'Button'}</button>`
	}

	getEditorCategory(): string {
		return 'Core'
	}
}
